// /admin → Safety: how often the app's Safety pill is opened and each of its
// four numbers tapped (api/safety-log.js).
//
// Totals and a per-day table, and nothing else, because that is all there is:
// the app sends only which row, never who or from where — one of the rows is
// the Klinic Crisis Line. Loads itself, like the Final exams tab: a small
// record that has no business riding the dashboard payload.

import { useCallback, useEffect, useState } from 'react';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';

export const SAFETY_ROWS = [
  ['open', 'Safety button opened'],
  ['security', 'Campus Security'],
  ['safewalk', 'Safe Walk'],
  ['police', 'Non-emergency police'],
  ['klinic', 'Klinic Crisis Line'],
];

/** The last `n` days ending at `today` (YYYY-MM-DD), newest first. */
export function recentDays(today, n) {
  const [y, m, d] = String(today || '').split('-').map(Number);
  if (!y || !m || !d) return [];
  const end = Date.UTC(y, m - 1, d);
  return Array.from({ length: n }, (_, i) => new Date(end - i * 86400000).toISOString().slice(0, 10));
}

export default function SafetyAdmin({ token }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [span, setSpan] = useState(14);

  const load = useCallback(async () => {
    if (!token) return;
    setErr('');
    try {
      const r = await fetch(`${API_BASE}/api/safety-log?admin=1`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.ok) setData(j);
      else setErr(j.error || `Could not load the counts (${r.status}).`);
    } catch (e) {
      setErr(`Could not load the counts: ${e.message}`);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const days = data ? recentDays(data.today, span) : [];
  const calls = data ? SAFETY_ROWS.slice(1).reduce((n, [k]) => n + (data.totals?.[k] || 0), 0) : 0;

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div>
          <div style={S.title}>Safety button</div>
          <div style={S.note}>
            Counts from the app (26.11.0 and later), only from phones with Help improve UMaps on. Only totals are
            kept — no session, phone or location — so nothing here can say who tapped what.
          </div>
        </div>
        <button style={S.btn} onClick={load}>Refresh</button>
      </div>

      {err && <div style={S.err}>{err}</div>}
      {!data && !err && <div style={S.note}>Loading…</div>}

      {data && (
        <>
          <div style={S.tiles}>
            {SAFETY_ROWS.map(([k, label]) => (
              <div key={k} style={{ ...S.tile, ...(k === 'open' ? S.tileLead : {}) }} data-testid={`safety-total-${k}`}>
                <div style={S.tileValue}>{(data.totals?.[k] || 0).toLocaleString()}</div>
                <div style={S.tileLabel}>{label}</div>
              </div>
            ))}
          </div>
          <div style={S.note}>
            {calls.toLocaleString()} number{calls === 1 ? '' : 's'} tapped in all. A tap opens the phone&rsquo;s
            dialler; whether the call was placed is not something the app can see.
          </div>

          <div style={S.spanRow}>
            {[7, 14, 30, 90].map((n) => (
              <button key={n} style={{ ...S.chip, ...(span === n ? S.chipOn : {}) }} onClick={() => setSpan(n)}>
                {n} days
              </button>
            ))}
          </div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Day</th>
                {SAFETY_ROWS.map(([k, label]) => <th key={k} style={S.thNum}>{label}</th>)}
              </tr>
            </thead>
            <tbody>
              {days.map((day) => {
                const row = data.days?.[day] || {};
                return (
                  <tr key={day}>
                    <td style={S.td}>{day}</td>
                    {SAFETY_ROWS.map(([k]) => <td key={k} style={S.tdNum}>{row[k] || 0}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

const S = {
  wrap: { maxWidth: 980 },
  head: { display: 'flex', gap: 16, alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 },
  title: { fontSize: 18, fontWeight: 800, color: '#202124' },
  note: { fontSize: 13, color: '#5f6368', marginTop: 6, maxWidth: 680 },
  err: { color: '#c5221f', fontSize: 13, margin: '8px 0' },
  btn: { border: '1px solid #dadce0', background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13 },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginTop: 8 },
  tile: { border: '1px solid #e8eaed', borderRadius: 10, padding: '12px 14px', background: '#fff' },
  tileLead: { background: '#e8f0fe', borderColor: '#d2e3fc' },
  tileValue: { fontSize: 24, fontWeight: 800, color: '#202124' },
  tileLabel: { fontSize: 12, color: '#5f6368', marginTop: 2 },
  spanRow: { display: 'flex', gap: 6, margin: '18px 0 8px' },
  chip: { border: '1px solid #dadce0', background: '#fff', borderRadius: 999, padding: '4px 10px', fontSize: 12, cursor: 'pointer' },
  chipOn: { background: '#1a73e8', borderColor: '#1a73e8', color: '#fff' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff' },
  th: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e8eaed', color: '#5f6368', fontWeight: 700 },
  thNum: { textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e8eaed', color: '#5f6368', fontWeight: 700 },
  td: { padding: '6px 8px', borderBottom: '1px solid #f1f3f4', fontVariantNumeric: 'tabular-nums' },
  tdNum: { textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f1f3f4', fontVariantNumeric: 'tabular-nums' },
};
